import { z } from 'zod'

import { AddressSchema } from './address'

export const BillingAuthorizationTypeSchema = z.enum([
  'PURCHASE_ORDER',
  'BUDGET_APPROVAL',
  'PORTAL_REFERENCE',
  'EMAIL_APPROVAL',
  'OTHER',
])

export type BillingAuthorizationType = z.infer<
  typeof BillingAuthorizationTypeSchema
>

export const BillingAuthorizationTypeLabels: Record<
  BillingAuthorizationType,
  string
> = {
  PURCHASE_ORDER: 'Purchase order',
  BUDGET_APPROVAL: 'Budget approval',
  PORTAL_REFERENCE: 'Portal reference',
  EMAIL_APPROVAL: 'Email approval',
  OTHER: 'Other',
}

export const AuthorizationAmountPolicySchema = z.enum([
  'HARD_CAP',
  'WARNING_ONLY',
  'NO_CAP',
])

export type AuthorizationAmountPolicy = z.infer<
  typeof AuthorizationAmountPolicySchema
>

export const AuthorizationAmountPolicyLabels: Record<
  AuthorizationAmountPolicy,
  string
> = {
  HARD_CAP: 'Hard cap',
  WARNING_ONLY: 'Warning only',
  NO_CAP: 'No cap',
}

const OptionalEmailSchema = z
  .string()
  .trim()
  .email('Invalid email address')
  .optional()
  .or(z.literal(''))
  .transform(value => (value === '' ? undefined : value))

export const EntityBillingProfileSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  entityId: z.string().uuid(),
  apContactId: z.string().uuid().nullable().optional(),
  billingEmail: z.string().email().nullable().optional(),
  invoiceCcEmails: z.array(z.string().email()).default([]),
  billingAddress: AddressSchema.nullable().optional(),
  paymentTerms: z.string().nullable().optional(),
  paymentMethod: z.string().nullable().optional(),
  invoiceNotes: z.string().nullable().optional(),
  vendorPortalUrl: z.string().url().nullable().optional(),
  vendorPortalInstructions: z.string().nullable().optional(),
  lateFeeTerms: z.string().nullable().optional(),
  requiresBillingAuthorization: z.boolean().default(false),
  defaultBillingAuthorizationType: BillingAuthorizationTypeSchema.nullable().optional(),
  authorizationAmountPolicy: AuthorizationAmountPolicySchema.default('HARD_CAP'),
  requiresInvoiceApproval: z.boolean().default(false),
  // 1:N (invoicing-ops Phase 2): label distinguishes sibling profiles (null
  // renders as "Default"); exactly one live default per entity.
  label: z.string().nullable().optional(),
  isDefault: z.boolean().default(false),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable().optional(),
})

export type EntityBillingProfile = z.infer<typeof EntityBillingProfileSchema>

export const UpsertEntityBillingProfileSchema = z.object({
  apContactId: z.string().uuid().nullable().optional(),
  billingEmail: OptionalEmailSchema.nullable().optional(),
  invoiceCcEmails: z.array(z.string().trim().email('Invalid CC email')).default([]),
  billingAddress: AddressSchema.nullable().optional(),
  paymentTerms: z.string().trim().nullable().optional(),
  paymentMethod: z.string().trim().nullable().optional(),
  invoiceNotes: z.string().nullable().optional(),
  vendorPortalUrl: z
    .string()
    .trim()
    .url('Invalid vendor portal URL')
    .nullable()
    .optional()
    .or(z.literal('').transform(() => null)),
  vendorPortalInstructions: z.string().nullable().optional(),
  lateFeeTerms: z.string().nullable().optional(),
  requiresBillingAuthorization: z.boolean().default(false),
  defaultBillingAuthorizationType: BillingAuthorizationTypeSchema.nullable().optional(),
  authorizationAmountPolicy: AuthorizationAmountPolicySchema.optional(),
  requiresInvoiceApproval: z.boolean().default(false),
  label: z.string().trim().min(1).max(120).nullable().optional(),
})

export type UpsertEntityBillingProfile = z.infer<
  typeof UpsertEntityBillingProfileSchema
>

export const UpdateEntityBillingProfileSchema =
  UpsertEntityBillingProfileSchema.partial()

export type UpdateEntityBillingProfile = z.infer<
  typeof UpdateEntityBillingProfileSchema
>

// Creating an additional (non-default) profile: a label is required so
// pickers always have something to show for it.
export const CreateEntityBillingProfileSchema =
  UpsertEntityBillingProfileSchema.extend({
    label: z.string().trim().min(1).max(120),
  })

export type CreateEntityBillingProfile = z.infer<
  typeof CreateEntityBillingProfileSchema
>
